import React from 'react';
import * as Sentry from '@sentry/nextjs';

import { GetServerSidePropsResult, GetStaticPropsContext } from 'next';
import { NextSeo } from 'next-seo';
import getPublicConfig from '@/utils/getPublicConfig';

import { BlogPostPage as BlogPostPageView } from '@/domains/blogs';
import ResourcesStore from '@/stores/ResourcesStore';
import { Resource } from '@/types/Content/Resources';
import { ContentType } from '@/common/constants/ContentTypes';
import { isValidContentType } from '@/utils/isValidContentType';
import ProductStore from '@/stores/ProductStore';

interface BlogPostPageProps {
  resource: Resource;
  contentType: ContentType;
}

const publicRuntimeConfig = getPublicConfig();

const BlogPostPage = ({
  resource,
  contentType,
}: BlogPostPageProps): JSX.Element => {
  const { title, excerpt, image, seo, slug } = resource ?? {};

  const blogPostSeo = {
    title: seo?.title || title || '',
    description: seo?.description || excerpt || '',
  };

  const ogUrl = `${publicRuntimeConfig.LOCATION_ORIGIN}/${ContentType[contentType]}/${slug}`;

  const openGraph = {
    type: 'article',
    site_name: 'OneVillage',
    url: ogUrl,
    images: [
      {
        url: image?.url,
      },
    ],
    ...blogPostSeo,
  };

  return (
    <>
      <NextSeo
        openGraph={openGraph}
        title={blogPostSeo.title}
        description={blogPostSeo.description}
        twitter={{
          cardType: 'summary_large_image',
        }}
      />
      <BlogPostPageView resource={resource} contentType={contentType} />
    </>
  );
};

export async function getServerSideProps(
  context: GetStaticPropsContext,
): Promise<GetServerSidePropsResult<BlogPostPageProps>> {
  const { params = {} } = context ?? {};
  const { slug = '' } = params;
  const contentType = params.contentType as ContentType;

  try {
    let resource;
    if (!isValidContentType(contentType as string)) {
      return {
        notFound: true,
      };
    }

    if (contentType === ContentType.RESOURCES && slug) {
      resource = await ResourcesStore.getResourceBySlug(slug as string);
    }

    if (contentType === ContentType.PRODUCTS) {
      resource = await ProductStore.getProduct(slug as string);
    }

    const relatedGroups = await ResourcesStore.getRelatedGroupsBySlug(
      slug as string,
    );

    if (!resource) {
      return {
        notFound: true,
      };
    }

    return {
      props: {
        contentType,
        resource: { ...resource, relatedGroups },
      },
    };
  } catch (error) {
    Sentry.captureException(error);

    return {
      notFound: true,
    };
  }
}

export default BlogPostPage;
